import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { lighten } from "@mui/system";
import { Typography, List, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar } from "@mui/material";
import { IconLogout, IconUserFilled, IconChevronRight, IconChevronDown, IconSettings, IconFileText, IconChecklist, IconSpeakerphone, IconReceipt2, IconShieldLock, IconMail, IconLayoutDashboard, IconStar, IconArticle } from "@tabler/icons-react";

import { useAuthentication } from "@redux/authentication/hook";

import "./layout.css"

import { MODULE_GUID } from "@utils/constant";

const menuList = [
  {
    isGroup: false,
    title: "Post",
    path: "post",
    moduleGuid: MODULE_GUID.POST,
    icon: <IconFileText />,
  },
  {
    isGroup: false,
    title: "Applications",
    path: "applications",
    moduleGuid: MODULE_GUID.APPLICATION,
    icon: <IconChecklist />,
  },
  {
    isGroup: false,
    title: "Featured",
    path: "featured",
    moduleGuid: MODULE_GUID.POST,
    icon: <IconStar />,
  },
  // {
  //   isGroup: false,
  //   title: "Blog",
  //   path: "blog",
  //   moduleGuid: MODULE_GUID.POST,
  //   icon: <IconArticle />,
  // },
  {
    isGroup: true,
    title: "Payments",
    moduleGuid: MODULE_GUID.PAYMENT,
    icon: <IconReceipt2 />,
    children: [
      {
        title: "Activity",
        path: "activity-payment",
        groupName: "Payments",
      },
      {
        title: "Donation",
        path: "donation-payment",
        groupName: "Payments",
      },
      {
        title: "Featured Donation",
        path: "featured-donation",
        groupName: "Payments",
      },
    ],
  },
  {
    isGroup: false,
    title: "Broadcasts",
    path: "broadcasts",
    moduleGuid: MODULE_GUID.BROADCAST,
    icon: <IconSpeakerphone />,
  },
  {
    isGroup: true,
    icon: <IconSettings />,
    title: "Setup",
    moduleGuid: MODULE_GUID.SETUP,
    children: [
      {
        title: "Banner Setup",
        path: "banner-setup",
        groupName: "Setup",
      },
      {
        title: "Bank Setup",
        path: "bank-setup",
        groupName: "Setup",
      },
      {
        title: "Fund Setup",
        path: "fund-setup",
        groupName: "Setup",
      },
      {
        title: "Question Setup",
        path: "question-setup",
        groupName: "Setup",
      },
      {
        title: "Form Setup",
        path: "form-setup",
        groupName: "Setup",
      },
    ],
  },
  {
    isGroup: false,
    title: "Security",
    path: "security",
    moduleGuid: MODULE_GUID.SECURITY,
    icon: <IconShieldLock />,
  },
  {
    isGroup: false,
    title: "Inquiries",
    path: "inquiries",
    moduleGuid: MODULE_GUID.INQUIRY,
    icon: <IconMail />,
  },
  {
    isGroup: true,
    icon: <IconLayoutDashboard />,
    title: "About Us",
    moduleGuid: MODULE_GUID.ABOUT_US,
    children: [
      {
        title: "Section 1",
        path: "section-1",
        groupName: "About Us",
      },
      {
        title: "Section 2",
        path: "section-2",
        groupName: "About Us",
      },
      {
        title: "Section 3",
        path: "section-3",
        groupName: "About Us",
      },
      {
        title: "Section 4",
        path: "section-4",
        groupName: "About Us",
      },
    ],
  },
];

const findPath = (menuList, basePath) => {
  for (const item of menuList) {
    if (item.path === basePath) {
      return item;
    }
    if (item.children) {
      const result = findPath(item.children, basePath);
      if (result) return result;
    }
  }
  return null;
}

export default function Navbar({
  setOpen,
  isDrawer,
}) {
  const location = useLocation();
  const {
    user,
    deauthenticateUser,
    moduleAccess,
  } = useAuthentication();

  const [selectedMenu, setSelectedMenu] = useState(null);
  const [categoryExpand, setCategoryExpand] = useState([]);

  const accessMenuList = useMemo(() => {
    const authenticatedMenuList = menuList?.filter((menu) =>
      moduleAccess?.some((m) => m?.moduleGuid === menu?.moduleGuid)
    );

    return authenticatedMenuList;
  }, [moduleAccess]);

  useEffect(() => {
    if (!location || !location.pathname) return;
  
    const basePath = location.pathname.startsWith("/")
      ? location.pathname.split("/")[2]
      : location.pathname;
    const selectedItem = findPath(menuList, basePath);
    setSelectedMenu(selectedItem);
  }, [location, menuList]);

  useEffect(() => {
    setCategoryExpand(
      menuList
        ?.filter((x) => x.isGroup)
        ?.map((x) => {
          return {
            title: x.title,
            open: false,
          };
        })
    );
  }, [menuList]);

  const handleSignOut = () => {
    deauthenticateUser()
  }

  const drawer = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", marginLeft: "20px", marginRight: "18px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "20px",
        }}
      >
        <Avatar
          sx={{ backgroundColor: "#E5EDEF", width: "3rem", height: "3rem" }}
        >
          <IconUserFilled color={"#7CA7AD"} />
        </Avatar>
        <div>
          <Typography fontSize={14} color="#202020">{user?.fullName}</Typography>
          {/* <Typography fontSize={13} color="#202020">{user?.fullName}</Typography> */}
          {/* <Typography fontSize={11} color="#999999">Super Admin</Typography> */}
        </div>
      </div>
      <Divider sx={{ backgroundColor: "#ffffff" }} />

      <List sx={{p: 0, pt: "20px", pb: "20px"}} className="navbar-list">
        {accessMenuList?.map((menuItem, index) => {
          if (menuItem.isGroup) {
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setCategoryExpand((prevCategoryExpand) => {
                      // Find the item to update
                      const updatedCategoryExpand = prevCategoryExpand?.map(
                        (item) => {
                          if (item.title === menuItem.title) {
                            // Toggle the `open` state of the matched item
                            return { ...item, open: !item.open };
                          }
                          // Return other items unchanged
                          return item;
                        }
                      );

                      return updatedCategoryExpand;
                    });
                  }}
                  sx={{
                    backgroundColor:
                      menuItem.title === selectedMenu?.groupName
                        ? "#7CA7AD"
                        : "none",
                    ":hover": {
                      backgroundColor:
                        menuItem.title === selectedMenu?.groupName
                          ? lighten("#7CA7AD", 0.15) // Lighten by 15%
                          : "none",
                    },
                    borderRadius: "5px",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      marginRight: "1.4rem",
                      color:
                        menuItem.title === selectedMenu?.groupName
                          ? "#ffffff"
                          : "#5D5D5D",
                    }}
                  >
                    {menuItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        color={
                          menuItem.title === selectedMenu?.groupName
                            ? "#ffffff"
                            : "#5D5D5D"
                        }
                        fontSize={"0.875rem"}
                      >
                        {menuItem.title}
                      </Typography>
                    }
                  />
                  <div style={{ marginLeft: "auto" }}>
                    {categoryExpand?.find((x) => x.title === menuItem.title)
                      ?.open === false ? (
                      <IconChevronRight
                        size={15}
                        color={
                          menuItem.title === selectedMenu?.groupName
                            ? "#ffffff"
                            : "#5D5D5D"
                        }
                      />
                    ) : (
                      <IconChevronDown
                        size={15}
                        color={
                          menuItem.title === selectedMenu?.groupName
                            ? "#ffffff"
                            : "#5D5D5D"
                        }
                      />
                    )}
                  </div>
                </ListItemButton>

                {categoryExpand.find((x) => x.title === menuItem.title)?.open &&
                  menuItem.children?.map((subItem, index) => (
                    <ListItemButton
                      key={index}
                      component={RouterLink}
                      to={subItem.path}
                      onClick={() => {
                        setSelectedMenu(subItem);
                        isDrawer && setOpen(false);
                      }}
                      sx={{
                        padding: "0.5rem 1rem",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            fontSize={"0.875rem"}
                            sx={{
                              pl: 6,
                              color:
                                subItem.title === selectedMenu?.title
                                  ? "#7CA7AD"
                                  : "#5D5D5D",
                            }}
                          >
                            {subItem.title}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
              </div>
            );
          }

          // Not grouped menu
          return (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ListItemButton
                component={RouterLink}
                to={menuItem.path}
                onClick={() => {
                  setSelectedMenu(menuItem);
                  isDrawer && setOpen(false);
                }}
                sx={{
                  backgroundColor:
                    menuItem.title === selectedMenu?.title
                      ? "#7CA7AD"
                      : "none",
                    ":hover": {
                      backgroundColor:
                        menuItem.title === selectedMenu?.title
                          ? lighten("#7CA7AD", 0.15) // Lighten by 15%
                          : "none",
                    },
                    borderRadius: "5px",
                  }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    marginRight: "1.4rem",
                    color:
                      menuItem.title === selectedMenu?.title
                        ? "#ffffff"
                        : "#5D5D5D",
                  }}
                  >
                    {menuItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        color={
                          menuItem.title === selectedMenu?.title
                            ? "#ffffff"
                            : "#5D5D5D"
                        }
                        fontSize={"0.875rem"}
                      >
                        {menuItem.title}
                      </Typography>
                    }
                  />
                  <IconChevronRight
                    size={15}
                    color={
                      menuItem.title === selectedMenu?.title
                        ? "#ffffff"
                        : "#5D5D5D"
                    }
                  />
                </ListItemButton>
            </div>
          );
          
        })}
      </List>

      <div style={{ marginTop: "auto" }}>
        <Divider sx={{ backgroundColor: "#ffffff"}} />
        <List>
          <ListItemButton
              onClick={() => handleSignOut()}
          >
            <ListItemIcon
              sx={{ color: "#FF5555", minWidth: 0, marginRight: "1.4rem" }}
            >
              <IconLogout />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color={"#FF5555"} fontSize={"0.875rem"}>
                  Sign Out
                </Typography>
              }
            />
          </ListItemButton>
        </List>
      </div>
    </div>
  );

  return (
    <div
      style={{
        height: "100%",
        // width: "100%",
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #E5E5E5",
      }}
    >
      {drawer}
    </div>
  );
}
